import { Button } from '@mantine/core';
import { modals } from '@mantine/modals';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { IntegrationResponse } from '@/api';
import { adapterName } from '@/components/Integrations/adapters/copy';
import {
  capabilitiesFor,
  useIntegrationCapabilities,
} from '@/components/Integrations/capabilities';
import { ConfirmDelete } from '@/components/Ui/ConfirmDelete';
import { EmptyText } from '@/components/Ui/EmptyText';
import { LedgerRow } from '@/components/Ui/LedgerRow';
import { Page } from '@/components/Ui/Page';
import { PageHeader } from '@/components/Ui/PageHeader';
import { Section } from '@/components/Ui/Section';
import { useUndoToast } from '@/components/Ui/Toast';
import { useLanguageAwareRouting } from '@/hooks/useLanguageAwareRouting';
import { useRustyState } from '@/state';
import classes from '../AccountScreens.module.css';
import { errorStatus } from '../format';
import { IntegrationEditor } from './IntegrationEditor';
import { usedByTitles } from './integration-text';
import { SigningSecret } from './SigningSecret';

export function EditIntegrationScreen() {
  const { id = '' } = useParams();
  // Keyed so another integration's page starts from fresh state.
  return <EditIntegration key={id} id={id} />;
}

function EditIntegration({ id }: { id: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { createLanguageURL } = useLanguageAwareRouting();
  const toast = useUndoToast();
  const caps = useIntegrationCapabilities();
  const [integration, setIntegration] = useState<IntegrationResponse | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'missing' | 'failed'>('loading');
  const [rotated, setRotated] = useState<string | null>(null);
  const [rotating, setRotating] = useState(false);
  // Remounts the editor with fresh initial values after a save.
  const [version, setVersion] = useState(0);

  const api = useRustyState.getState().api;

  const load = useCallback(async () => {
    try {
      const row = await useRustyState.getState().api.getIntegration(id);
      setIntegration(row);
      setState('ready');
    } catch (e) {
      setState(errorStatus(e) === 404 ? 'missing' : 'failed');
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const toList = () => navigate(createLanguageURL('/account/integrations'));

  if (state !== 'ready' || !integration) {
    return (
      <Page width="narrow">
        <PageHeader
          title={t('integrations.editTitle')}
          breadcrumb={[{ label: t('integrations.pageTitle'), to: '/account/integrations' }]}
        />
        {state === 'loading' ? (
          <EmptyText>{t('ui.loading')}</EmptyText>
        ) : (
          <p className={classes.errorText}>
            {state === 'missing'
              ? t('integrations.editor.notFound')
              : t('integrations.index.loadFailed')}
          </p>
        )}
      </Page>
    );
  }

  const c = capabilitiesFor(caps, integration.kind);
  const signed = integration.signed ?? c.signed;
  const formCount = integration.form_count;

  const rotate = () =>
    modals.openConfirmModal({
      title: t('integrations.signing.rotateTitle'),
      children: <p style={{ margin: 0 }}>{t('integrations.signing.rotateBody')}</p>,
      labels: { confirm: t('integrations.signing.rotateConfirm'), cancel: t('ui.cancel') },
      onConfirm: async () => {
        setRotating(true);
        try {
          const res = await api.rotateIntegrationSecret(integration.id, { onSuccess: false });
          setRotated(res.hmac_secret_b64);
        } finally {
          setRotating(false);
        }
      },
    });

  return (
    <Page width="narrow">
      <PageHeader
        title={integration.title}
        subtitle={adapterName(integration.kind, t)}
        breadcrumb={[{ label: t('integrations.pageTitle'), to: '/account/integrations' }]}
        actions={
          <Button
            component={Link}
            to={createLanguageURL(`/account/integrations/${integration.id}/deliveries`)}
            variant="default"
          >
            {t('integrations.index.deliveries')}
          </Button>
        }
      />

      <Section title={t('integrations.editor.settingsTitle')}>
        <IntegrationEditor
          key={version}
          kind={integration.kind}
          mode="edit"
          initial={{ title: integration.title, config: integration.config ?? {} }}
          onBack={toList}
          onUpdate={async (body) => {
            if (Object.keys(body).length === 0) {
              toast(t('integrations.editor.unchanged'));
              return;
            }
            await api.updateIntegration(integration.id, body, { onSuccess: false });
            toast(t('integrations.editor.saved'));
            await load();
            setVersion((v) => v + 1);
          }}
        />
      </Section>

      {signed && (
        <Section title={t('integrations.signing.sectionTitle')}>
          {rotated ? (
            <SigningSecret
              secret={rotated}
              signatureHeader={c.signature_header}
              onDone={() => setRotated(null)}
            />
          ) : (
            <LedgerRow
              label={t('integrations.signing.label')}
              value={t('integrations.signing.value')}
              help={t('integrations.signing.rotateHelp')}
              action={
                <Button variant="subtle" size="xs" onClick={rotate} loading={rotating}>
                  {t('integrations.signing.rotate')}
                </Button>
              }
            />
          )}
        </Section>
      )}

      <Section title={t('integrations.editor.deleteTitle')}>
        <LedgerRow
          label={t('integrations.editor.deleteLabel')}
          value={
            formCount
              ? t('integrations.editor.deleteUsed', {
                  count: formCount,
                  titles: usedByTitles(integration.form_titles, formCount, t),
                })
              : t('integrations.editor.deleteUnused')
          }
          action={
            <ConfirmDelete
              label={t('integrations.editor.deleteButton')}
              size="xs"
              description={
                formCount
                  ? t('integrations.editor.deleteConfirmUsed', { count: formCount })
                  : t('integrations.editor.deleteConfirm')
              }
              confirmLabel={t('integrations.editor.deleteConfirmButton')}
              onConfirm={async () => {
                await api.deleteIntegration(integration.id);
                void useRustyState.getState().refreshProblemsCount();
                toast(t('integrations.editor.deleted', { title: integration.title }));
                toList();
              }}
            />
          }
        />
      </Section>
    </Page>
  );
}
